import {
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
  Box,
  Snackbar,
  SnackbarContent,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState, useCallback } from "react";
import * as Yup from "yup";
import {
  ButtonContainer,
  EmployeeContainter,
  InputContainer,
  ModalContainer,
} from "./styles";
import {
  createNewEmployee,
  getEmployee,
  updateEmployee,
} from "src/services/employee";
import EmployeeList from "../EmployeeList";

interface IEmployeeModal {
  name: string;
  active: boolean;
  id: number;
}

interface INewEmployeeModal {
  open: boolean;
  handleClose: () => void;
}

const validationSchema = Yup.object({
  name: Yup.string().required("Nome é obrigatório"),
  active: Yup.boolean(),
});

export default function NewEmployeeModal({
  open,
  handleClose,
}: INewEmployeeModal) {
  const [employee, setEmployee] = useState<IEmployeeModal[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [savingEmployee, setSavingEmployee] = useState<boolean>(false);
  const [toast, setToast] = useState<any>(null);
  const [editingEmployee, setEditingEmployee] = useState<IEmployeeModal | null>(
    null,
  );

  const getLoggedUserRole = useCallback(() => {
    const stored = localStorage.getItem("meData");
    if (!stored) return null;
    try {
      const { role } = JSON.parse(stored);
      return role;
    } catch {
      return null;
    }
  }, []);

  const listEmployee = async () => {
    if (getLoggedUserRole() !== "admin") return;

    try {
      setLoading(true);
      const response = await getEmployee();
      setEmployee(response.data);
    } catch (error) {
      setToast({ message: "Erro ao carregar usuários", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee: IEmployeeModal) => {
    setEditingEmployee(employee);
  };

  const handleSubmit = async (
    values: IEmployeeModal,
    resetForm: () => void,
  ) => {
    if (getLoggedUserRole() !== "admin") {
      setToast({
        message: "Acesso negado: você não é um administrador.",
        type: "error",
      });
      return;
    }

    try {
      setSavingEmployee(true);

      const payload = {
        name: values.name,
        active: values.active,
      };

      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, payload);
        setToast({
          message: "Funcionário atualizado com sucesso! ✅",
          type: "success",
        });
      } else {
        await createNewEmployee(payload);
        setToast({
          message: "Funcionário criado com sucesso! ✅",
          type: "success",
        });
      }

      resetForm();
      setEditingEmployee(null);
      listEmployee();
    } catch (error) {
      setToast({ message: "Erro ao salvar funcionário ❌", type: "error" });
    } finally {
      setSavingEmployee(false);
    }
  };


  useEffect(() => {
    if (open) {
      const role = getLoggedUserRole();
      if (role !== "admin") {
        alert(
          "Acesso restrito. Apenas administradores podem acessar esta área.",
        );
        handleClose();
      } else {
        listEmployee();
      }
    }
  }, [open, handleClose, getLoggedUserRole]);

  if (open && getLoggedUserRole() !== "admin") return null;

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <EmployeeContainter>
            <Typography component="h2" variant="h6" gutterBottom>
              {editingEmployee
                ? "Editar Funcionário/Técnico"
                : "Novo Funcionário/Técnico"}
            </Typography>

            <Formik
              enableReinitialize
              initialValues={{
                name: editingEmployee?.name || "",
                active: editingEmployee?.active ?? true,
              }}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) =>
                handleSubmit(values as any, resetForm)
              }
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                setFieldValue,
              }) => (
                <Form>
                  <InputContainer>
                    <TextField
                      fullWidth
                      required
                      label="Nome"
                      size="small"
                      margin="normal"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      disabled={!!editingEmployee}
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.active}
                          onChange={(e) =>
                            setFieldValue("active", e.target.checked)
                          }
                        />
                      }
                      label="Usuário ativo"
                    />
                  </InputContainer>

                  <ButtonContainer>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleClose();
                        setEditingEmployee(null);
                      }}
                      disabled={savingEmployee}
                    >
                      Cancelar
                    </Button>

                    <Button
                      variant="contained"
                      type="submit"
                      disabled={savingEmployee}
                    >
                      {savingEmployee ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        "Salvar"
                      )}
                    </Button>
                  </ButtonContainer>

                  {loading ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <EmployeeList employees={employee} onEdit={handleEdit} />
                  )}
                </Form>
              )}
            </Formik>
          </EmployeeContainter>
        </ModalContainer>
      </Modal>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      </Snackbar>
    </>
  );
}
